import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

interface StakeholderInterface {
  id: string;
  title: string;
  description: string;
  gif_url: string;
  demo_url: string;
  order_index: number;
}

export const StakeholderInterfacesEditor = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: interfaces, isLoading } = useQuery({
    queryKey: ['stakeholder-interfaces'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stakeholder_interfaces')
        .select('*')
        .order('order_index');
      
      if (error) throw error;
      return data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: Partial<StakeholderInterface> & { id: string }) => {
      const { error } = await supabase
        .from('stakeholder_interfaces')
        .update(values)
        .eq('id', values.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stakeholder-interfaces'] });
      toast.success('Interface updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update interface: ' + error.message);
    }
  });

  const handleFileUpload = async (file: File, interfaceId: string) => {
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('legal_documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('legal_documents')
        .getPublicUrl(filePath);

      await updateMutation.mutateAsync({
        id: interfaceId,
        gif_url: publicUrl
      });

      setSelectedFile(null);
    } catch (error) {
      toast.error('Failed to upload file: ' + error.message);
    }
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>, id: string) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const updates = {
      id,
      title: String(formData.get('title')),
      description: String(formData.get('description')),
      demo_url: String(formData.get('demo_url')),
    };

    try {
      await updateMutation.mutateAsync(updates);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Stakeholder Interfaces</h3>
      <div className="space-y-6">
        {interfaces?.map((interface_) => (
          <Card key={interface_.id} className="p-6">
            <form onSubmit={(e) => handleUpdate(e, interface_.id)} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  name="title"
                  defaultValue={interface_.title}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  name="description"
                  defaultValue={interface_.description}
                  required
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Demo URL</Label>
                <Input
                  name="demo_url"
                  defaultValue={interface_.demo_url}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Interface Image</Label>
                <div className="flex items-center gap-4">
                  <img
                    src={interface_.gif_url}
                    alt={interface_.title}
                    className="w-32 h-20 object-cover rounded"
                  />
                  <Input
                    type="file"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleFileUpload(e.target.files[0], interface_.id);
                      }
                    }}
                    accept="image/*"
                    className="flex-1"
                  />
                </div>
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </form>
          </Card>
        ))}
      </div>
    </div>
  );
};